import React, { useContext, useState, useEffect } from "react";
import { AppContext } from "../../contexts/AppContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ThemeContext, ThemeContextData } from "../../contexts/ThemeContext";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Banner from "../banner/Banner";
import { LIGHT_THEME } from "../../constants/ThemeConstants";
import { FaSun, FaMoon } from "react-icons/fa";
import { FiMenu, FiX, FiChevronRight } from "react-icons/fi";
import UserMenu from "../UserMenu";

const DarkModeToggle = ({ toggleTheme }) => {
  const themeContext = useContext(ThemeContext);
  const isLightTheme = themeContext === LIGHT_THEME;

  return (
    <button
      onClick={toggleTheme}
      className="relative w-14 h-7 rounded-full bg-brand-800/20 border border-[var(--color-border-default)] transition-colors duration-300 focus-ring"
      aria-label={isLightTheme ? "Switch to dark mode" : "Switch to light mode"}
    >
      <motion.div
        className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center"
        animate={{ x: isLightTheme ? 2 : 26 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        {isLightTheme ? (
          <FaSun className="w-3.5 h-3.5 text-yellow-500" />
        ) : (
          <FaMoon className="w-3.5 h-3.5 text-brand-400" />
        )}
      </motion.div>
    </button>
  );
};

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about-us", label: "About Us" },
  { to: "/programs", label: "Programs" },
  { to: "/courses", label: "Pre Recorded Courses" },
  { to: "/blogs", label: "Blogs" },
  { to: "/contact-us", label: "Contact" },
];

function Header({ toggleTheme }) {
  const appContext = useContext(AppContext);
  const { user, logout, authLoading } = useContext(AuthContext);
  const themeContext = useContext(ThemeContext);
  const theme = ThemeContextData[themeContext];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      <Banner />
      <header
        className={`sticky top-0 z-40 transition-all duration-300 min-h-[56px] ${
          scrolled
            ? "glass shadow-nav py-2"
            : `${theme.backgroundSecondary} py-3`
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 focus-ring">
              <img
                src={appContext.logo}
                className={`transition-all duration-300 w-auto ${scrolled ? "h-10" : "h-14"}`}
                alt={appContext.name}
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-1" aria-label="Main navigation">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `
                    relative px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 focus-ring
                    ${isActive
                      ? "text-brand-500"
                      : `${theme.textNormal} hover:${theme.textFocus} hover:bg-brand-500/5`
                    }
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      {isActive && (
                        <motion.div
                          layoutId="nav-indicator"
                          className="absolute bottom-0 left-3 right-3 h-0.5 bg-brand-500 rounded-full"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Desktop Right Actions */}
            <div className="hidden md:flex items-center space-x-3">
              {!authLoading && !user && (
                <Link
                  to="/sign-in"
                  className="px-4 py-2 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors duration-200 focus-ring"
                >
                  Sign In
                </Link>
              )}
              {!authLoading && user && <UserMenu user={user} logout={logout} />}
              <DarkModeToggle toggleTheme={toggleTheme} />
            </div>

            {/* Mobile Right Actions */}
            <div className="flex md:hidden items-center space-x-3">
              <DarkModeToggle toggleTheme={toggleTheme} />
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2 rounded-lg text-[var(--color-text-primary)] hover:bg-brand-500/10 transition-colors focus-ring"
                aria-label="Open menu"
              >
                <FiMenu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-[85%] max-w-sm shadow-modal flex flex-col"
              style={{ backgroundColor: "var(--color-bg-primary)" }}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-4 border-b border-[var(--color-border-default)]">
                <img src={appContext.logo} className="h-10 w-auto" alt={appContext.name} />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-lg hover:bg-brand-500/10 transition-colors focus-ring"
                  aria-label="Close menu"
                >
                  <FiX className="w-6 h-6 text-[var(--color-text-primary)]" />
                </button>
              </div>

              {/* Drawer Nav */}
              <nav className="flex-1 overflow-y-auto py-4 px-3" aria-label="Mobile navigation">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NavLink
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => `
                        flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-medium transition-colors duration-200
                        ${isActive
                          ? "bg-brand-500/10 text-brand-500"
                          : "text-[var(--color-text-primary)] hover:bg-brand-500/5"
                        }
                      `}
                    >
                      {item.label}
                      <FiChevronRight className="w-4 h-4 opacity-40" />
                    </NavLink>
                  </motion.div>
                ))}
              </nav>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-[var(--color-border-default)] space-y-3">
                {!authLoading && user && (
                  <div className="space-y-1">
                    <p className="px-4 py-2 text-sm font-semibold text-[var(--color-text-secondary)] truncate">
                      {user.name}
                    </p>
                    {[
                      { label: "My Programs & Purchases", to: "/dashboard" },
                      { label: "Profile", to: "/profile" },
                      ...(user.role === "admin" ? [{ label: "Admin Dashboard", to: "/admin" }] : []),
                    ].map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-[var(--color-text-primary)] hover:bg-brand-500/5 transition-colors"
                      >
                        {item.label}
                        <FiChevronRight className="w-4 h-4 opacity-40" />
                      </Link>
                    ))}
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        logout();
                        navigate("/");
                      }}
                      className="flex items-center w-full px-4 py-3 rounded-xl text-base font-medium text-red-500 hover:bg-red-500/5 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
                {!authLoading && !user && (
                  <Link
                    to="/sign-in"
                    onClick={() => setMobileOpen(false)}
                    className="block w-full text-center px-4 py-3 text-base font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-colors focus-ring"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
